import { API_URL_CLIENT, API_URL_CLIENT_FALLBACK } from './config/api';

type TelemetryKind = 'error' | 'unhandledrejection' | 'react';

interface CaptureContext {
    kind?: TelemetryKind;
    componentStack?: string;
    source?: string;
    line?: number;
}

interface ErrorLike {
    message?: unknown;
    name?: unknown;
    stack?: unknown;
    requestId?: unknown;
}

interface TelemetryPayload {
    kind: TelemetryKind;
    event_id: string;
    session_id: string;
    release: string;
    route: string;
    source?: string;
    line?: number;
    message: string;
    stack?: string;
    component_stack?: string;
    api_request_id?: string;
    context: {
        workspace_id?: string;
        project_id?: string;
        viewport: string;
        online: boolean;
    };
}

const TELEMETRY_PATH = '/telemetry/client-error';
const SESSION_KEY = 'flowium_telemetry_session';
const DEDUPE_WINDOW_MS = 10_000;
const recentFingerprints = new Map<string, number>();
let globalHandlersInstalled = false;

const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const AUTH_PATTERN = /\b(?:Bearer|Token)\s+[A-Za-z0-9._~+\-/=]+/gi;
const SECRET_ASSIGNMENT_PATTERN = /\b(password|secret|token|authorization|session_id|api[_-]?key|access[_-]?key|credential|signature)\b(\s*[:=]\s*)([^\s,&}\]]+)/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

function createId(prefix: string): string {
    const randomPart = Math.random().toString(36).slice(2, 14);
    return `${prefix}-${Date.now().toString(36)}-${randomPart}`;
}

function redact(value: unknown, maxLength: number): string {
    return String(value || '')
        .replace(JWT_PATTERN, '[redacted-jwt]')
        .replace(AUTH_PATTERN, '[redacted-authorization]')
        .replace(SECRET_ASSIGNMENT_PATTERN, '$1$2[redacted]')
        .replace(EMAIL_PATTERN, '[redacted-email]')
        .slice(0, maxLength);
}

function safeStorageValue(storageName: 'localStorage' | 'sessionStorage', key: string): string | null {
    try {
        return window[storageName].getItem(key);
    } catch {
        return null;
    }
}

function getSessionId(): string {
    const existing = safeStorageValue('sessionStorage', SESSION_KEY);
    if (existing) return existing;
    const sessionId = createId('session');
    try {
        window.sessionStorage.setItem(SESSION_KEY, sessionId);
    } catch {
        // Session correlation remains valid for this event when storage is unavailable.
    }
    return sessionId;
}

export function getFrontendRelease(): string {
    const configured = import.meta.env.REACT_APP_RELEASE?.trim();
    if (configured) return redact(configured, 128);

    const scriptSource = Array.from(document.scripts)
        .map(script => script.src)
        .find(source => /\/static\/js\/main\.[a-z0-9]+\.js(?:$|\?)/.test(source));
    const match = scriptSource?.match(/\/static\/js\/(main\.[a-z0-9]+)\.js(?:$|\?)/);
    return match?.[1] || 'unknown';
}

function normalizeError(error: unknown): { name: string; message: string; stack?: string; requestId?: string } {
    if (error instanceof Error) {
        const withRequestId = error as Error & { requestId?: unknown };
        return {
            name: redact(error.name || 'Error', 128),
            message: redact(error.message || 'Unknown browser error', 2048),
            stack: error.stack ? redact(error.stack, 12000) : undefined,
            requestId: typeof withRequestId.requestId === 'string'
                ? redact(withRequestId.requestId, 128)
                : undefined,
        };
    }

    if (error && typeof error === 'object') {
        const candidate = error as ErrorLike;
        let serialized = Object.prototype.toString.call(error);
        try {
            serialized = JSON.stringify(error);
        } catch {
            // Circular or hostile values fall back to their object type.
        }
        return {
            name: redact(candidate.name || 'Error', 128),
            message: redact(candidate.message || serialized, 2048),
            stack: candidate.stack ? redact(candidate.stack, 12000) : undefined,
            requestId: typeof candidate.requestId === 'string'
                ? redact(candidate.requestId, 128)
                : undefined,
        };
    }

    return {
        name: 'Error',
        message: redact(error || 'Unknown browser error', 2048),
    };
}

function shouldSend(payload: TelemetryPayload): boolean {
    const fingerprint = [payload.kind, payload.message, payload.stack || ''].join('|');
    const now = Date.now();
    const lastSent = recentFingerprints.get(fingerprint);
    if (lastSent && now - lastSent < DEDUPE_WINDOW_MS) return false;

    recentFingerprints.set(fingerprint, now);
    if (recentFingerprints.size > 100) {
        for (const [key, timestamp] of recentFingerprints) {
            if (now - timestamp >= DEDUPE_WINDOW_MS) recentFingerprints.delete(key);
        }
        while (recentFingerprints.size > 100) {
            const oldest = recentFingerprints.keys().next().value;
            if (!oldest) break;
            recentFingerprints.delete(oldest);
        }
    }
    return true;
}

function telemetryEndpoints(): string[] {
    return Array.from(new Set([API_URL_CLIENT, API_URL_CLIENT_FALLBACK].filter(Boolean)))
        .map(baseUrl => `${baseUrl}${TELEMETRY_PATH}`);
}

async function sendTelemetry(payload: TelemetryPayload): Promise<void> {
    const authToken = safeStorageValue('localStorage', 'auth_token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-ID': payload.event_id,
        'X-Flowium-Release': payload.release,
    };
    if (authToken) headers.Authorization = `Bearer ${authToken}`;

    for (const endpoint of telemetryEndpoints()) {
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
                cache: 'no-store',
                keepalive: true,
            });
            if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
                return;
            }
        } catch {
            // Try the fallback endpoint without surfacing telemetry transport errors.
        }
    }
}

export function captureException(error: unknown, captureContext: CaptureContext = {}): void {
    if (!import.meta.env.PROD || typeof window === 'undefined') return;
    try {
        const normalized = normalizeError(error);
        const params = new URLSearchParams(window.location.search);
        const payload: TelemetryPayload = {
            kind: captureContext.kind || 'error',
            event_id: createId('event'),
            session_id: getSessionId(),
            release: getFrontendRelease(),
            route: window.location.pathname,
            source: captureContext.source
                ? captureContext.source.split('?', 1)[0].split('#', 1)[0]
                : undefined,
            line: captureContext.line,
            message: `${normalized.name}: ${normalized.message}`,
            stack: normalized.stack,
            component_stack: captureContext.componentStack
                ? redact(captureContext.componentStack, 6000)
                : undefined,
            api_request_id: normalized.requestId,
            context: {
                workspace_id: params.get('workspace') || undefined,
                project_id: params.get('project') || undefined,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                online: window.navigator.onLine,
            },
        };
        if (!shouldSend(payload)) return;
        void sendTelemetry(payload).catch(() => undefined);
    } catch {
        // Error reporting must never create a secondary application failure.
    }
}

export function installGlobalErrorHandlers(): void {
    if (globalHandlersInstalled || typeof window === 'undefined') return;
    globalHandlersInstalled = true;

    window.addEventListener('error', event => {
        captureException(event.error || event.message, {
            kind: 'error',
            source: event.filename,
            line: event.lineno,
        });
    });
    window.addEventListener('unhandledrejection', event => {
        captureException(event.reason, { kind: 'unhandledrejection' });
    });
}
